import React from "react";
import type { CSSProperties } from "react";
import { Editable } from "./Editable";
import { useLocale } from "@/contexts/LocaleContext";
import type { TranslationKey } from "@/lib/translations";

type Scope = "page" | "global";

/**
 * Convenience wrapper: combines `t(key)` with an inline-editable `<Editable>`.
 * Uses the translation key as the stable CMS id by default.
 */
type TextProps = {
  k: TranslationKey;
  id?: string;
  as?: keyof JSX.IntrinsicElements;
  multiline?: boolean;
  html?: boolean;
  className?: string;
  wrapperClassName?: string;
  scope?: Scope;
};

/**
 * Bereinigt Inline-Styles, Farb-Attribute und `<font>`-Tags, die aus dem
 * WYSIWYG-Editor (oder per Copy/Paste aus Word/Browser) hereinkommen können.
 * So bleibt das vom Design vorgegebene Styling immer erhalten.
 */
function sanitizeTranslationHtml(input: string): string {
  return input
    // <font color="..."> entfernen, Inhalt behalten
    .replace(/<\/?font[^>]*>/gi, "")
    // jegliches style="..." entfernen
    .replace(/\sstyle\s*=\s*"[^"]*"/gi, "")
    .replace(/\sstyle\s*=\s*'[^']*'/gi, "")
    // color="..." (legacy) entfernen
    .replace(/\scolor\s*=\s*"[^"]*"/gi, "")
    .replace(/\scolor\s*=\s*'[^']*'/gi, "");
}

export const EditableT: React.FC<TextProps> = ({
  k, id, as = "span", multiline, html, className, wrapperClassName, scope,
}) => {
  const { t } = useLocale();
  const value = t(k);
  // Auto-detect HTML (Übersetzungen aus dem WYSIWYG-Editor enthalten Tags wie <div>, <br>, &nbsp;).
  const looksLikeHtml = /<\/?[a-z][\s\S]*>|&nbsp;|&amp;|&#\d+;/i.test(value);
  const renderAsHtml = html || looksLikeHtml;
  // Plain-text Mehrzeiler: \n in <br/> umwandeln, damit Absätze sichtbar bleiben.
  const rawHtmlValue = renderAsHtml && !looksLikeHtml
    ? value.replace(/\n/g, "<br/>")
    : value;
  // Inline-Styles aus dem WYSIWYG-Editor entfernen, damit Design-Vorgaben (Farbe etc.) gewinnen.
  const htmlValue = renderAsHtml ? sanitizeTranslationHtml(rawHtmlValue) : rawHtmlValue;
  return (
    <Editable
      id={id ?? k}
      as={as}
      multiline={multiline ?? renderAsHtml}
      html={renderAsHtml}
      defaultValue={renderAsHtml ? htmlValue : value}
      className={className}
      wrapperClassName={wrapperClassName}
      scope={scope}
    >
      {renderAsHtml ? <span dangerouslySetInnerHTML={{ __html: htmlValue }} /> : value}
    </Editable>
  );
};

type LinkProps = {
  k: TranslationKey;
  href: string;
  id?: string;
  external?: boolean;
  className?: string;
  wrapperClassName?: string;
  style?: CSSProperties;
  scope?: Scope;
};

export const EditableTLink: React.FC<LinkProps> = ({
  k, href, id, external, className, wrapperClassName, style, scope,
}) => {
  const { t } = useLocale();
  return (
    <Editable
      id={id ?? k}
      type="link"
      label={t(k)}
      href={href}
      external={external}
      className={className}
      wrapperClassName={wrapperClassName}
      style={style}
      scope={scope}
    />
  );
};

type ImageProps = {
  id: string;
  src: string;
  alt?: string;
  className?: string;
  wrapperClassName?: string;
  style?: CSSProperties;
  scope?: Scope;
  href?: string;
  ariaLabel?: string;
  external?: boolean;
  linkClassName?: string;
};

export const EditableImage: React.FC<ImageProps> = (props) => (
  <Editable {...props} type="image" />
);
